import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { createAccount, checkUsernameAvailability, reserveUsername } from './auth';
import './Auth.css';

const AccountCreation = () => {
  const navigate = useNavigate();
  const { updateUser } = useContext(UserContext);
  
  // State
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    displayName: '', // Add display name field
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  
  // Form handling
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If username field is changing, reset availability status
    if (name === 'username' && usernameAvailable !== null) {
      setUsernameAvailable(null);
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Username availability check
  const handleUsernameBlur = async () => {
    if (formData.username && formData.username.length >= 3) {
      setUsernameChecking(true);
      const isAvailable = await checkUsernameAvailability(formData.username);
      setUsernameAvailable(isAvailable);
      setUsernameChecking(false);
    }
  };
  
  // Account creation
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Validate
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    
    if (!formData.username || formData.username.length < 3) {
      setError('Username must be at least 3 characters');
      setLoading(false);
      return;
    }
    
    if (!/^[a-zA-Z0-9._]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, periods, and underscores');
      setLoading(false);
      return;
    }
    
    if (!formData.displayName || formData.displayName.trim().length < 2) {
      setError('Display name must be at least 2 characters');
      setLoading(false);
      return;
    }
    
    if (!acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy to continue');
      setLoading(false);
      return;
    }
    
    // Check username availability one more time
    const isUsernameAvailable = await checkUsernameAvailability(formData.username);
    if (!isUsernameAvailable) {
      setError('Username is already taken. Please choose another one.');
      setLoading(false);
      return;
    }
    
    try {
      // Create account with proper display name
      const result = await createAccount(formData.email, formData.password, {
        username: formData.username,
        displayName: formData.displayName.trim(), // Use actual display name
        bio: 'Design enthusiast'
      });
      
      if (result.success) {
        // Now reserve username with the authenticated user's ID
        const reserveResult = await reserveUsername(formData.username, result.user.uid);
        if (!reserveResult.success) {
          console.error('Failed to reserve username after account creation:', reserveResult.error);
          // Don't fail the signup - just log the error since account was created successfully
        }
        
        // Update user in context
        updateUser({
          uid: result.user.uid,
          email: formData.email,
          username: formData.username,
          displayName: formData.displayName.trim(),
          emailVerified: false
        });
        
        // Show verification message
        setVerificationEmailSent(true);
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Unexpected error creating account:', error);
      setError('An unexpected error occurred during signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // If verification email sent
  if (verificationEmailSent) {
    return (
      <div className="auth-container centered">
        <div className="auth-form-container">
          <h2>Verify Your Email</h2>
          
          <div className="auth-message">
            <p>We've sent a verification email to <strong>{formData.email}</strong></p>
            <p>Please check your inbox and click the verification link to complete your registration.</p>
          </div>
          
          <p>
            After verifying your email, you can <button onClick={() => navigate('/login')} className="text-button">log in to your account</button> and customize your Digital Card.
          </p>
          
          <div className="auth-footer">
            <p>
              Didn't receive the email? <button onClick={() => setVerificationEmailSent(false)} className="text-button">Try again</button> or check your spam folder.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Signup form
  return (
    <div className="auth-container centered">
      <div className="auth-form-container">
        <h2>Create Account</h2>
        <p className="auth-description">
          Join Artifex to discover curated design content through your personalized Digital Card.
        </p>
        
        <div className="digital-card-info">
          <h4>Your Digital Card Will Be Generated</h4>
          <p className="digital-card-intro">
            Upon signup, a Digital Card will be created for you. This serves as your aesthetic passport 
            that learns your design preferences and can be used both publicly and privately across platforms.
          </p>
          
          <div className="feature-list">
            <div className="feature-item">
              <span className="feature-icon">🎨</span>
              <span>Personalizes content discovery based on your aesthetic preferences</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🌐</span>
              <span>Works as a portable login for other sites with privacy-focused email forwarding</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔒</span>
              <span>Available in public, private, and professional versions with customizable details</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">✨</span>
              <span>Features AI-generated backgrounds and customizable links to your portfolio, website, and contacts</span>
            </div>
          </div>
          
          <p className="digital-card-note">
            <em>You'll customize your card settings and privacy options after account creation.</em>
          </p>
        </div>
        
        {error && <div className="auth-error">{error}</div>}
        
        <form onSubmit={handleSignup} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="displayName">Full Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              placeholder="Enter your full name (e.g., Matt Goff)"
              required
            />
            <div className="input-helper">This is how your name will appear to other users.</div>
          </div>
          
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              onBlur={handleUsernameBlur}
              placeholder="Choose a unique username"
              pattern="^[a-zA-Z0-9._]+$"
              title="Only letters, numbers, periods, and underscores allowed"
              required
            />
            {usernameChecking && <div className="input-helper">Checking availability...</div>}
            {usernameAvailable === true && <div className="input-helper success">Username is available</div>}
            {usernameAvailable === false && <div className="input-helper error">Username is already taken</div>}
            <div className="input-helper">Only letters, numbers, periods, and underscores.</div>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Create a password"
              minLength="6"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              minLength="6"
              required
            />
          </div>
          
          <div className="form-checkbox">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              required
            />
            <label htmlFor="terms">
              I accept the <Link to="/terms" target="_blank" className="text-link">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-link">Privacy Policy</Link>
            </label>
          </div>
          
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading || usernameChecking || usernameAvailable === false}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <div className="auth-links">
          <p>
            Already have an account? <button onClick={() => navigate('/login')} className="text-button">Log in</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountCreation;